import classNames from "classnames";
import { Immutable } from "immer";
import React, { useContext } from "react";
import SVG from "react-inlinesvg";
import { usePageSetting } from "tsi-common-react/src/utils/settings";
import { t } from "ttag";

import { Context } from "@reactivated";

import { constants } from "@reactivated/constants";

import iconIconSearch from "../../assets/svg/icon-search.svg";

const AppSlug = constants["tsicommon.types.AppSlug"];

type IProps = Immutable<{
    isSearchOpen: boolean;
}> & {
    onOpenSearch: () => void;
    onCloseSearch: () => void;
};

export const SiteSearchBar = ({
    isSearchOpen,
    onOpenSearch,
    onCloseSearch,
}: IProps): React.ReactNode => {
    const openSearchButtonRef = React.useRef<HTMLButtonElement>(null);
    const searchButtonRef = React.useRef<HTMLButtonElement>(null);
    const hasInteracted = React.useRef(false);

    // Get locale-aware search URL from current language context
    const context = useContext(Context);
    const langCode = context.i18n.language.code;
    const langPickerEntries = context.site_settings.lang_picker;

    // Find the full locale code from lang_picker that matches the current language
    // context.i18n.language.code may be short (e.g., "fr") but URL patterns need
    // the full locale (e.g., "fr-ca")
    const currentLangEntry = langPickerEntries.find(
        (entry) =>
            entry.language_code === langCode ||
            entry.language_code.startsWith(`${langCode}-`),
    );
    const fullLangCode = currentLangEntry?.language_code ?? langCode;

    // Only include language prefix if there are multiple unique languages
    // This matches Django's i18n_patterns(prefix_default_language=...)
    const uniqueLanguages = new Set(
        langPickerEntries.map((entry) => entry.language_code),
    );
    const searchUrl =
        uniqueLanguages.size > 1 ? `/${fullLangCode}/search/` : "/search/";

    React.useEffect(() => {
        if (!hasInteracted.current) return;

        if (isSearchOpen && searchButtonRef.current) {
            searchButtonRef.current.focus();
        }

        if (!isSearchOpen && openSearchButtonRef.current) {
            openSearchButtonRef.current.focus();
        }
    }, [isSearchOpen]);

    const handleOpenSearch = () => {
        hasInteracted.current = true;
        onOpenSearch();
    };

    const handleCloseSearch = () => {
        hasInteracted.current = true;
        onCloseSearch();
    };

    const appSlug = usePageSetting("app_slug");
    const isCanada = appSlug === AppSlug.TEMPURCA;

    const buttonClasses = classNames({
        "site-search-bar__button": true,
        "site-search-bar__button--hide": isSearchOpen,
        "site-search-bar__button--canada": isCanada,
    });

    const buttonTextClasses = isCanada
        ? "site-search-bar__button-text--canada"
        : "";

    return (
        <div className="site-search-bar">
            <button
                ref={openSearchButtonRef}
                className={buttonClasses}
                type="button"
                onClick={handleOpenSearch}
            >
                <SVG src={iconIconSearch} role="img" aria-hidden="true" />
                <span className={buttonTextClasses}>{t`Search`}</span>
            </button>
            <form
                action={searchUrl}
                method="get"
                aria-hidden={!isSearchOpen}
                target="_top"
            >
                <button
                    ref={searchButtonRef}
                    type="submit"
                    aria-label={t`Search`}
                >
                    <SVG src={iconIconSearch} role="img" aria-hidden="true" />
                </button>

                <label
                    className="ada-screenreader-only"
                    htmlFor="top-search-nav-form"
                >
                    {t`Search`}
                </label>
                <input
                    id="top-search-nav-form"
                    type="text"
                    name="query"
                    title={t`Search Tempur-Pedic`}
                    placeholder={t`Search`}
                />

                <button
                    type="button"
                    onClick={handleCloseSearch}
                    className="close"
                    aria-label={t`Close Search Container`}
                >
                    <span aria-hidden="true">x</span>
                </button>
            </form>
        </div>
    );
};
